<template>
  <div id="app">
    <template v-if="isStudentPage">
      <!-- For student pages, render the router-view with conditional classes -->
      <router-view :class="{ 'student-survey-content': isSurveyPage, 'student-fullscreen-content': !isSurveyPage }" />
    </template>
    <template v-else>
      <!-- Original layout for non-student pages -->
      <Header @toggle-menu="toggleMenu" class="app-header" />

      <div class="main-content">
        <transition name="drawer">
          <SideMenu class="side-menu" v-if="showSideMenu" @logout="handleLogout" />
        </transition>

        <div
          v-if="isMobile && showSideMenu"
          class="drawer-backdrop"
          @click="closeMenu"
          aria-hidden="true"
        ></div>

        <div class="content">
          <router-view />
        </div>
      </div>

      <!-- Feedback button hidden for now; re-enable when the feature is ready. -->
      <!-- <FeedbackButton :is-visible="showFeedback" :user-id="currentUserId" @toggle="toggleFeedback" /> -->
    </template>
  </div>
</template>




<script>

/*
╔══════════════════════════════════════════════════════════════════════════════╗
║                  VUE.JS APPLICATION BOOTSTRAP - THE CORE TRIO                ║
╚══════════════════════════════════════════════════════════════════════════════╝

🚀 HOW A VUE.JS APP STARTS: THE THREE ESSENTIAL FILES

Every Vue.js application needs these three files working together:

┌─────────────────────────────────────────────────────────────────────────────┐
│ 1️⃣  index.html (in /public/)                                                │
├─────────────────────────────────────────────────────────────────────────────┤
│ • The ONLY actual HTML page in your entire app                             │
│ • Contains the <div id="app"></div> mounting point                         │
│ • Loads main.js via <script type="module">                                 │
│ • Stays the same for all routes - Vue handles the rest!                    │
│                                                                             │
│ Think of it as: The CONTAINER that holds everything                        │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ 2️⃣  main.js (in /src/)                                                      │
├─────────────────────────────────────────────────────────────────────────────┤
│ • The ENTRY POINT for your JavaScript application                          │
│ • Creates the Vue app instance: createApp(App)                             │
│ • Configures plugins (router, store, etc.)                                 │
│ • Mounts the app to #app: .mount('#app')                                   │
│                                                                             │
│ Think of it as: The ENGINE that starts everything                          │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ 3️⃣  App.vue (in /src/)                                                      │
├─────────────────────────────────────────────────────────────────────────────┤
│ • The ROOT COMPONENT of your application                                   │
│ • Contains the main layout structure (header, nav, footer)                 │
│ • Houses <router-view> where pages are dynamically loaded                  │
│ • Wraps all other components in your app                                   │
│                                                                             │
│ Think of it as: The SKELETON that holds your app structure                 │
└─────────────────────────────────────────────────────────────────────────────┘

🔄 THE STARTUP SEQUENCE:

  Browser loads → index.html
                     ↓
                  Executes main.js
                     ↓
                  Creates Vue app with App.vue
                     ↓
                  Mounts to <div id="app">
                     ↓
                  App.vue renders with router
                     ↓
                  Your application is LIVE! ✨

💡 KEY CONCEPTS:

  • Single Page Application (SPA): Only ONE HTML file (index.html)
  • Component-Based: App.vue is the root, everything else nests inside
  • Reactive Mounting: Vue replaces <div id="app"> with your App component
  • Router Magic: <router-view> swaps pages WITHOUT reloading the browser

📚 LEARN MORE: https://vuejs.org/guide/essentials/application.html

*/







import Header from './components/layout/Header.vue';
import SideMenu from './components/layout/SideMenu.vue';
import FeedbackButton from './components/layout/FeedbackButton.vue';

const MOBILE_BREAKPOINT = 768;

export default {
  name: 'App',
  components: {
    Header,
    SideMenu,
    FeedbackButton,
  },
  data() {
    const mobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
    return {
      loggedIn: false,
      classes: [],
      isMobile: mobile,
      showSideMenu: !mobile,
      showFeedback: false,
    };
  },
  mounted() {
    console.log("%cWelcome to Swiss Eating Quiz, Folks!", "font-size: 24px; font-weight: bold;");
    console.log("%cIf you're interested in joining our team, please reach out to us", "font-size: 16px;");
    window.addEventListener('resize', this.checkViewport);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkViewport);
  },
  watch: {
    isMobile(nowMobile) {
      // When viewport flips between mobile and desktop, sync the drawer:
      // mobile → close it, desktop → open it.
      this.showSideMenu = !nowMobile;
    },
    $route() {
      // On mobile, close the drawer after navigation so the user sees the new page.
      if (this.isMobile) {
        this.showSideMenu = false;
      }
    },
  },
  computed: {
    isStudentPage() {
      const studentRoutes = ['/studentloginpage', '/studentsurveypage', '/studentambiancesurveypage', '/studenttextsurveypage', '/studentsurveycompletepage'];
      return studentRoutes.some(route => this.$route.path.startsWith(route));
    },
    isSurveyPage() {
      return this.$route.path.includes('surveypage');
    },
    currentUserId() {
      try {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        return currentUser.id || null;
      } catch {
        return null;
      }
    }
  },
  methods: {
    checkViewport() {
      this.isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    },
    toggleMenu() {
      this.showSideMenu = !this.showSideMenu;
    },
    closeMenu() {
      this.showSideMenu = false;
    },
    handleLogin() {
      this.loggedIn = true;
      localStorage.setItem('loggedIn', 'true');
      this.$router.push('/');
    },
    handleLogout() {
      this.loggedIn = false;
      localStorage.removeItem('loggedIn');
      this.$router.push('/login');
    },
    updateClasses(updatedClasses) {
      this.classes = updatedClasses;
    },
    toggleFeedback() {
      this.showFeedback = !this.showFeedback;
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css');

html,
body,
#app {
  height: 100%;
  margin: 0;
  padding: 0;
}

#app {
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.app-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: #f8f9fa;
}

.main-content {
  display: flex;
  height: calc(100vh - 60px);
  /* Adjust based on your header height */
  overflow: hidden;
  position: relative;
}

/* Mobile drawer behavior — applies below 768px */
.drawer-backdrop {
  display: none;
}

@media (max-width: 768px) {
  .side-menu {
    position: fixed;
    top: 60px;
    left: 0;
    bottom: 0;
    z-index: 1001;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);
  }

  .drawer-backdrop {
    display: block;
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1000;
    animation: drawer-backdrop-fade-in 0.2s ease;
  }

  @keyframes drawer-backdrop-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  /* Slide-in animation only on mobile */
  .drawer-enter-active,
  .drawer-leave-active {
    transition: transform 0.25s ease;
  }
  .drawer-enter-from,
  .drawer-leave-to {
    transform: translateX(-100%);
  }
}

.side-menu {
  width: 250px;
  background-color: #f4f4f4;
  overflow-y: auto;
  flex-shrink: 0;
}

.content {
  flex: 1;
  overflow-y: auto;
}

/*---------- New styles for student pages ----------*/

/* Styles for student survey pages */
.student-survey-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
}

/* Styles for student login and complete pages */
.student-fullscreen-content {
  width: 100%;
  height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
}

/* Common styles for all student pages */
.student-survey-content,
.student-fullscreen-content {
  color: #2c3e50;
}

.student-survey-content h1,
.student-fullscreen-content h1 {
  color: #42b983;
}

.student-survey-content .styled-button,
.student-fullscreen-content .styled-button {
  background-color: #42b983;
  color: white;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 4px;
}

.student-survey-content .styled-button:hover,
.student-fullscreen-content .styled-button:hover {
  background-color: #3a9d72;
}

.student-survey-content .styled-input,
.student-fullscreen-content .styled-input {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 20px;
}

.student-survey-content table,
.student-fullscreen-content table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.student-survey-content th,
.student-survey-content td,
.student-fullscreen-content th,
.student-fullscreen-content td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: center;
}

.student-survey-content th,
.student-fullscreen-content th {
  background-color: #f4f4f4;
}
</style>
